import { ButtonHTMLAttributes, ReactNode } from 'react';
import classnames from 'classnames';
import { FontIcon, FontIconName } from '../FontIcon';
import { Spinner } from '../Spinner';
import styles from './button.module.sass';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children?: ReactNode;
    /** If `true` background is set to be partially transparent */
    inverse?: boolean;
    /** Secondary background color is applied */
    secondary?: boolean;
    /** If `true` padding is reduced, making button smaller */
    compact?: boolean;
    small?: boolean;
    /** Adds styles for square button with icon only, no text */
    social?: boolean;
    round?: boolean;
    /** Transparent bg, `width: auto` */
    plain?: boolean;
    /** Allows the button to grow to the width of its container */
    fullWidth?: boolean;
    /**
     * Component (e.g. NextJS' `Link`) passed from application level and used to render link
     * This way Footer links do not depend on NextJS (or some other library) and can be configured from the parent
     */
    LinkComponent?: any;
    href?: string;
    to?: string;
    icon?: FontIconName;
    /** Buttons in header have different size styles */
    headerButton?: boolean;
    contactsButton?: boolean;
    /** Displays spinner inside the button; disables button */
    loading?: boolean;
    target?: string;
    state?: any;
    white?: boolean;
}

export const Button = ({
    LinkComponent,
    children,
    disabled,
    inverse,
    secondary,
    compact,
    social,
    small,
    round,
    plain,
    fullWidth,
    headerButton,
    contactsButton,
    loading,
    icon,
    white,
    className,
    ...htmlProps
}: ButtonProps) => {
    const iconMarkup =
        icon && !loading ? (
            <div className={styles.iconWrapper}>
                <FontIcon name={icon} />
            </div>
        ) : null;

    const spinner = loading ? (
        <span className={styles.spinner}>
            <Spinner inline />
        </span>
    ) : null;

    const content = (
        <>
            {!loading && children}
            {iconMarkup}
            {spinner}
        </>
    );

    const isDisabled = disabled || loading;
    const classNames = classnames(
        {
            [styles.inverse]: inverse,
            [styles.compact]: compact,
            [styles.fullWidth]: fullWidth,
            [styles['button-head']]: headerButton,
            [styles['contacts-button']]: contactsButton,
            [styles.social]: social,
            [styles.small]: small,
            [styles.round]: round,
            [styles.secondary]: secondary,
            [styles.plain]: plain,
            [styles.white]: white,
        },
        styles.button,
        className
    );

    const props = {
        className: classNames,
        disabled: isDisabled,
        ...htmlProps,
    };

    return LinkComponent ? <LinkComponent {...props}>{content}</LinkComponent> : <button {...props}>{content}</button>;
};
